/**
 * Which screen this tablet opens on: the tables, or the list of orders.
 *
 * A fact about where the tablet stands, like the lock delay, so it is kept on
 * the device. The tablet on the floor wants the tables; the one on the counter
 * wants the orders. Unset, it opens on the tables when the restaurant has any.
 */

import { useSyncExternalStore } from 'react';

const KEY = 'pos.home';

export type Home = 'tables' | 'orders';
export type HomeChoice = Home | 'auto';

const listeners = new Set<() => void>();

function stored(): HomeChoice {
  try {
    const raw = localStorage.getItem(KEY);
    return raw === 'tables' || raw === 'orders' ? raw : 'auto';
  } catch {
    return 'auto';
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  window.addEventListener('storage', listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', listener);
  };
}

export function useHomeChoice(): [HomeChoice, (next: HomeChoice) => void] {
  const choice = useSyncExternalStore(subscribe, stored);
  return [
    choice,
    (next) => {
      try {
        if (next === 'auto') localStorage.removeItem(KEY);
        else localStorage.setItem(KEY, next);
      } catch {
        // Storage refused: the choice holds for this page only.
      }
      for (const listener of listeners) listener();
    },
  ];
}

/** The screen to open on, given whether the restaurant has any tables. */
export function homeFor(choice: HomeChoice, hasTables: boolean): Home {
  if (choice !== 'auto') return choice;
  return hasTables ? 'tables' : 'orders';
}
